import dayjs from 'dayjs';
import jalaliday from 'jalaliday';
import relativeTime from 'dayjs/plugin/relativeTime';
import { DESCRIPTION_CONFIG } from '../constants';

dayjs.extend(jalaliday);
dayjs.extend(relativeTime);

// Date utilities
export const calculateDuration = (startDate, endDate) => {
  if (!startDate || !endDate) return 0;
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  return end.diff(start, 'day') + 1;
};

export const formatDate = (date, format = 'MMM DD, YYYY') => {
  return dayjs(date).format(format);
};

export const getRelativeTime = (date) => {
  return dayjs(date).fromNow();
};

// Task utilities
export const calculateSubtaskProgress = (subtasks) => {
  if (!subtasks || subtasks.length === 0) return 0;
  const completed = subtasks.filter(st => st.completed).length;
  return Math.round((completed / subtasks.length) * 100);
};

export const getTaskProgress = (column) => {
  switch (column) {
    case 'done':
      return 100;
    case 'in-progress':
      return 50;
    default:
      return 0;
  }
};

export const getCompletionStatus = (column) => {
  switch (column) {
    case 'done':
      return 'Completed';
    case 'in-progress':
      return 'In Progress';
    default:
      return 'Not Started';
  }
};

// Description utilities
export const getDescriptionStats = (description) => {
  if (!description) return { lines: 0, words: 0, characters: 0 };
  
  const lines = description.split('\n').length;
  const words = description.split(/\s+/).filter(word => word.length > 0).length;
  const characters = description.length;
  
  return { lines, words, characters };
};

export const getDescriptionPreview = (description) => {
  if (!description) return null;

  const { PREVIEW_LENGTH, PREVIEW_LINES } = DESCRIPTION_CONFIG;

  if (description.length <= PREVIEW_LENGTH && !description.includes('\n')) {
    return description;
  }

  // For multi-line descriptions, take first few lines
  const lines = description.split('\n').slice(0, PREVIEW_LINES);
  let preview = lines.join(' ');

  // If still too long, truncate
  if (preview.length > PREVIEW_LENGTH) {
    preview = preview.substring(0, PREVIEW_LENGTH).trim() + '...';
  }

  return preview;
};

export const shouldShowReadMore = (description) => {
  if (!description) return false;
  
  const { PREVIEW_LENGTH, PREVIEW_LINES } = DESCRIPTION_CONFIG;
  
  return description.length > PREVIEW_LENGTH || 
         description.split('\n').length > PREVIEW_LINES;
};

// File utilities
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const getFileType = (filename) => {
  const extension = filename.split('.').pop().toLowerCase();
  const typeMap = {
    'pdf': 'PDF',
    'doc': 'Word',
    'docx': 'Word',
    'xls': 'Excel',
    'xlsx': 'Excel',
    'ppt': 'PowerPoint',
    'pptx': 'PowerPoint',
    'jpg': 'Image',
    'jpeg': 'Image',
    'png': 'Image',
    'gif': 'Image',
    'svg': 'Image',
    'txt': 'Text',
    'zip': 'Archive',
    'rar': 'Archive'
  };
  return typeMap[extension] || 'File';
};

// Validation utilities
export const validateTask = (task) => {
  const errors = [];
  
  if (!task.content || task.content.trim().length === 0) {
    errors.push('Task title is required');
  }
  
  if (!task.assignee) {
    errors.push('Assignee is required');
  }
  
  if (!task.startDate || !task.endDate) {
    errors.push('Start and end dates are required');
  }
  
  if (task.startDate && task.endDate && dayjs(task.startDate).isAfter(dayjs(task.endDate))) {
    errors.push('Start date cannot be after end date');
  }
  
  return errors;
};

// ID generation
export const generateId = (prefix = '') => {
  return `${prefix}${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Array utilities
export const moveArrayItem = (array, fromIndex, toIndex) => {
  const newArray = [...array];
  const [movedItem] = newArray.splice(fromIndex, 1);
  newArray.splice(toIndex, 0, movedItem);
  return newArray;
};

// Color utilities
export const getContrastColor = (hexColor) => {
  // Remove # if present
  const color = hexColor.replace('#', '');
  
  // Convert to RGB
  const r = parseInt(color.substr(0, 2), 16);
  const g = parseInt(color.substr(2, 2), 16);
  const b = parseInt(color.substr(4, 2), 16);
  
  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  return luminance > 0.5 ? '#000000' : '#ffffff';
};

// Local storage utilities
export const saveToLocalStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

export const loadFromLocalStorage = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error('Error loading from localStorage:', error);
    return defaultValue;
  }
};
